import React, { useEffect, useRef, useState } from 'react';
import '../SummaryCard.scss';
import useStore, { IStore } from 'app/store';
import {
    Text,
    Link,
    createTheme,
    Separator,
    IIconProps,
    IconButton,
    FontIcon,
    IContextualMenuProps,
    Shimmer,
    IContextualMenuItem
} from '@fluentui/react';
import { useHistory } from 'react-router-dom';
import { useGetUserSavedLists } from 'app/services/user-api';
import { useListSummary } from 'app/services/request-api';
import { ListFolderDto } from 'generated/user-backend';
import {
    ListBuilderFilter2,
    ListQueryDto,
    PropertyFilter,
    RequestListBuilderFilter,
    TeamFilter
} from 'generated/request-backend';
import ListBuilderCardFolder from './ListBuilderCardFolder';
import { StateData } from '../../manageRequests/ManageRequests';

interface IProps {
    stateData: StateData;
    cardExpanded: boolean;
    setCardExpanded: (expanded: boolean) => void;
}

const ListBuilderCard: React.FC<IProps> = ({ stateData, cardExpanded, setCardExpanded }) => {
    const cardRef = useRef<HTMLDivElement>(null);
    const cardBodyRef = useRef<HTMLDivElement>(null);

    const history = useHistory();

    const myTheme = createTheme({
        fonts: {
            medium: {
                fontFamily: 'Monaco, Menlo, Consolas',
                fontSize: '20px'
            }
        },
        palette: {
            neutralLighter: '#C7E0F4'
        }
    });

    const kebabIcon: IIconProps = { iconName: 'MoreVertical' };

    const onMenuItemClick = (ev: React.MouseEvent<HTMLElement, MouseEvent> | React.KeyboardEvent<HTMLElement>, item: IContextualMenuItem) => {
        if (item.key === 'newList') {
            history.push('/list-builder');
        } else {
            history.push('/manage-lists');
        }
    };

    const menuProps: IContextualMenuProps = {
        items: [
            {
                key: 'newList',
                text: 'New List'
            },
            {
                key: 'manageLists',
                text: 'Manage Lists'
            }
        ],
        isBeakVisible: false,
        onItemClick: onMenuItemClick
    };

    // state
    const [folders, setFolders] = useState<ListFolderDto[]>([]);
    const [listQueries, setListQueries] = useState<ListQueryDto[]>([]);
    const filterPanelDisabled = useStore((state: IStore) => state.filterPanelDisabled);

    // mutations and queries
    const { data: savedLists, isLoading: isUserListsLoading } = useGetUserSavedLists();
    const { data: listSummary, isLoading: isListSummaryLoading } = useListSummary(listQueries);

    useEffect(() => {
        if (savedLists) {
            // Saved Lists is coming from useGetUserSavedList, which has not been updated to use generated code yet. TODO: Update this once useGetUserSavedLists is updated.
            savedLists.listFolders.forEach((folder: ListFolderDto) => {
                folder.savedListQueries.forEach((query) => {
                    const newListQuery = new ListQueryDto({
                        ...query,
                        createdOn: new Date(query.createdOn),
                        queryFilters: new RequestListBuilderFilter({
                            teamFilters: new TeamFilter({ ...query.queryFilters.teamFilters}),
                            propertyFilters: query.queryFilters.propertyFilters.map(propFilt => {
                                return new PropertyFilter({
                                    ...propFilt,
                                    filter: new ListBuilderFilter2({ ...propFilt.filter})
                                });
                            })
                        })
                    });
                    listQueries.push(newListQuery);
                });
            });
            setListQueries(listQueries);
            setFolders(savedLists.listFolders);
        }
    }, [savedLists]);

    const onToggleShow = () => {
        setCardExpanded(false);
    };

    useExpandDismiss(cardRef, onToggleShow);

    return (
        <div className={`lb-card ${cardExpanded ? 'card-expanded' : ''}`} ref={cardRef}>
            <div className="lb-card-header">
                <div className="lb-card-header-text">
                    <Text variant="xLarge">
                        LISTS {filterPanelDisabled && <FontIcon iconName="LocationDot" className="lb-card-active-icon" />}
                    </Text>
                    <IconButton className="lb-card-menu" ariaLabel="Lists options" iconProps={kebabIcon} menuProps={menuProps} />
                </div>
                <Separator theme={myTheme} />
            </div>
            <div className={`lb-card-body ${cardExpanded ? 'card-expanded' : ''}`} ref={cardBodyRef}>
                {isUserListsLoading || isListSummaryLoading ? (
                    <div className="lb-card-body-loading">
                        <Shimmer />
                        <Shimmer width="75%" />
                        <Shimmer width="50%" />
                    </div>
                ) : folders.length > 0 ? (
                    folders.map((folder, index) => (
                        <ListBuilderCardFolder key={index} folderData={folder} stateData={stateData} listSummary={listSummary} />
                    ))
                ) : (
                    <div className={'lb-card-no-items'}>
                        <Text block variant="large" className="lb-card-welcome">
                            Welcome!
                        </Text>
                        <Text block>
                            You haven't created any Lists yet. Visit{' '}
                            <Link href="/admin-training" underline={true}>
                                Portal Training
                            </Link>{' '}
                            to learn more or click{' '}
                            <Link href="/list-builder" underline={true}>
                                here
                            </Link>{' '}
                            to build your first list
                        </Text>
                    </div>
                )}
            </div>
            {folders.length > 0 && !isUserListsLoading && cardBodyRef?.current?.clientHeight >= 200 && (
                <div className="lb-card-footer" onClick={() => setCardExpanded(!cardExpanded)}>
                    <Text>{!cardExpanded ? 'Expand' : 'Collapse'}</Text>
                    <FontIcon className="lb-card-footer-icon" iconName={!cardExpanded ? 'ChevronDown' : 'ChevronUp'} />{' '}
                </div>
            )}
        </div>
    );
};

export default ListBuilderCard;

const useExpandDismiss = (ref: React.RefObject<HTMLDivElement>, onToggleShow: () => void) => {
    React.useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (ref.current && !ref.current.contains(event.target as Node)) {
                onToggleShow();
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            // Unbind the event listener on clean up
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [ref]);
};
